import { Icons } from '@/libs/components/src/lib/icons';
import { Button } from '@chiroup/components/Button';
import { Textarea } from '@chiroup/components/form/Textarea';
import type { PatientBalanceInfo } from '@chiroup/core/types/Patient.type';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useDevtools from '../../../hooks/useDevtools';

type RecalcFormValues = {
  sPatientIds: string;
  batchSize: number;
};

type RecalcResult = {
  patientId: string;
  clinicId: number;
  success: boolean;
  durationMs: number;
  balance?: PatientBalanceInfo;
  error?: string;
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const PatientPanel = () => {
  const { recalculateBalances } = useDevtools('patient-panel') as any;

  const { handleSubmit, control, watch, setValue } = useForm<RecalcFormValues>({
    defaultValues: {
      sPatientIds: '',
      batchSize: 5,
    },
  });

  const batchSize = watch('batchSize');

  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<RecalcResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [batchDuration, setBatchDuration] = useState<number | null>(null);
  const [batchStartTime, setBatchStartTime] = useState<number | null>(null);
  const cancelRef = useRef(false);

  const commentOutProcessedLine = (originalInput: string, targetLine: string): string => {
    const allLines = originalInput.split('\n');
    const updatedLines = allLines.map(line => {
      const trimmedLine = line.trim();

      if (trimmedLine === targetLine && !line.startsWith('# ')) {
        return `# ${line}`;
      }

      return line;
    });

    return updatedLines.join('\n');
  };

  const onSubmit = async (data: RecalcFormValues) => {
    if (!data.sPatientIds) return;

    const allLines = data.sPatientIds.split('\n');
    const lines = allLines
      .map(line => line.trim())
      .filter(line => line.length > 0 && !line.startsWith('# '));

    if (lines.length === 0) return;

    const size = Math.max(1, Math.min(10, data.batchSize || 5));

    setIsProcessing(true);
    cancelRef.current = false;
    setResults([]);
    setCurrentIndex(0);
    setBatchDuration(null);

    const batchStart = Date.now();
    setBatchStartTime(batchStart);

    const allResults: RecalcResult[] = [];
    let currentTextareaContent = data.sPatientIds;

    for (let i = 0; i < lines.length; i += size) {
      if (cancelRef.current) break;

      const batchLines = lines.slice(i, i + size);
      setCurrentIndex(i + batchLines.length);

      try {
        const batchResult = await recalculateBalances({
          patientIds: batchLines,
        });

        if (batchResult.success && batchResult.results) {
          for (let j = 0; j < batchResult.results.length; j++) {
            const result = batchResult.results[j];
            allResults.push(result);
            currentTextareaContent = commentOutProcessedLine(currentTextareaContent, batchLines[j]);
          }

          setResults([...allResults]);
          setValue('sPatientIds', currentTextareaContent);
        }
      } catch (error: any) {
        for (const line of batchLines) {
          allResults.push({
            patientId: line,
            clinicId: 0,
            success: false,
            durationMs: 0,
            error: error.message || 'Batch processing failed',
          });
          currentTextareaContent = commentOutProcessedLine(currentTextareaContent, line);
        }

        setResults([...allResults]);
        setValue('sPatientIds', currentTextareaContent);
      }

      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setBatchDuration(Date.now() - batchStart);
    setIsProcessing(false);
    setCurrentIndex(0);
  };

  const handleStop = () => {
    cancelRef.current = true;
  };

  const successful = results.filter(r => r.success).length;
  const failed = results.filter(r => !r.success).length;

  return (
    <div className="p-4 space-y-6">
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="bg-white border border-gray-400 rounded-lg">
          <div className="p-4 flex flex-row justify-between items-center">
            <div className="flex flex-col">
              <h1 className="font-extrabold">Recalculate Patient Balances</h1>
              {isProcessing && (
                <div className="text-sm text-gray-600 mt-1 space-y-1">
                  <div>Processing {currentIndex}...</div>
                  {batchStartTime && (
                    <div className="font-mono text-xs">
                      Batch time: {formatDuration(Date.now() - batchStartTime)}
                      {results.length > 0 &&
                        ` | Avg: ${formatDuration(results.reduce((sum, r) => sum + (r.durationMs || 0), 0) / results.length)}`}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 items-center">
              {isProcessing ? (
                <Button text="Stop" onClick={handleStop} type="button" icon={Icons.pause} />
              ) : (
                <>
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded border">
                    Batch: {batchSize || 5}
                  </div>
                  <Button
                    text="Go!"
                    disabled={isProcessing}
                    loading={isProcessing}
                    type="submit"
                    icon={Icons.puzzle}
                  />
                </>
              )}
            </div>
          </div>

          <div className="p-4 m-4 border border-gray-400 bg-gray-50 rounded-lg text-sm space-y-4">
            <p>
              Recalculates patient balances by running <code>getPatientBillingBalance</code> with{' '}
              <code>savePatientBalance: true</code> for each patient ID. The clinic is resolved
              automatically from the patient row.
            </p>
            <p>
              One patient ID per line. Processed items will be commented out with &apos;# &apos;.
            </p>
          </div>

          <div className="p-4 flex flex-col space-y-4">
            <label className="block text-sm font-medium text-gray-900">
              Batch Size (1-10)
              <input
                type="number"
                min={1}
                max={10}
                value={batchSize}
                onChange={e => setValue('batchSize', Number(e.target.value))}
                className="mt-1 block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <Controller
              name="sPatientIds"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  label="Patient IDs (one per line)"
                  rows={10}
                  className="font-mono text-xs"
                  placeHolder="215f6077-fd95-416e-8d0b-be7f7d92c497"
                />
              )}
            />
          </div>
        </section>
      </form>

      {results.length > 0 && (
        <section className="bg-white border border-gray-400 rounded-lg">
          <div className="p-4">
            <h2 className="font-bold mb-2">
              Results: {successful} successful, {failed} failed
              {batchDuration && ` (${formatDuration(batchDuration)})`}
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Patient ID</th>
                    <th className="text-right p-2">Clinic</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-right p-2">Balance</th>
                    <th className="text-right p-2">Patient</th>
                    <th className="text-right p-2">Payor</th>
                    <th className="text-right p-2">Credit</th>
                    <th className="text-right p-2">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i} className={`border-b ${r.success ? '' : 'bg-red-50'}`}>
                      <td className="p-2 font-mono text-xs">{r.patientId}</td>
                      <td className="p-2 text-right">{r.clinicId || '-'}</td>
                      <td className="p-2">
                        {r.success ? (
                          <span className="text-green-600">OK</span>
                        ) : (
                          <span className="text-red-600">{r.error}</span>
                        )}
                      </td>
                      <td className="p-2 text-right">{r.balance?.balance ?? '-'}</td>
                      <td className="p-2 text-right">{r.balance?.patientBalance ?? '-'}</td>
                      <td className="p-2 text-right">{r.balance?.payorBalance ?? '-'}</td>
                      <td className="p-2 text-right">{r.balance?.creditBalance ?? '-'}</td>
                      <td className="p-2 text-right">{formatDuration(r.durationMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PatientPanel;
